import { localized, msg } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/popover/sp-popover.js';

export interface ContextMenuItem {
  label: string;
  key: string;
  handler(): void;
  subitems?: ContextMenuItem[];
}

@localized()
export class CustomMenuElement extends LitElement {
  static get properties() {
    return {
      items: { type: Array },
      onHide: { type: Function },
    };
  }

  declare items: ContextMenuItem[];
  declare onHide: () => void;

  constructor() {
    super();
    this.items = [];
    this.onHide = () => null;
  }

  static styles = css`
    :host {
      position: absolute;
      min-width: 180px;
      z-index: 10000;
      opacity: 0;
      transform: scale(0.95);
      transition:
        opacity 0.15s ease,
        transform 0.15s ease;
    }
    :host(.show) {
      opacity: 1;
      transform: scale(1);
    }
    :host([hidden]) {
      display: none;
    }
    sp-popover {
      min-width: 180px;
    }
  `;

  private outsideClickListening = false;

  private readonly handleOutsideClick = (e: MouseEvent) => {
    if (!this.contains(e.target as Node)) {
      this.hide();
    }
  };

  private addOutsideClickListener() {
    if (this.outsideClickListening) return;
    document.addEventListener('click', this.handleOutsideClick);
    this.outsideClickListening = true;
  }

  private removeOutsideClickListener() {
    if (!this.outsideClickListening) return;
    document.removeEventListener('click', this.handleOutsideClick);
    this.outsideClickListening = false;
  }

  show() {
    this.hidden = false;
    this.classList.remove('show');
    setTimeout(() => {
      if (!this.hidden) this.classList.add('show');
    }, 10);
    setTimeout(() => {
      if (!this.hidden) this.addOutsideClickListener();
    }, 0);
  }

  private hide() {
    if (this.hidden) return;
    this.hidden = true;
    this.classList.remove('show');
    this.removeOutsideClickListener();
    this.onHide();
  }

  private handleItemClick(item: ContextMenuItem) {
    if (item.subitems && item.subitems.length > 0) return;
    item.handler();
    this.hide();
  }

  protected firstUpdated() {
    this.show();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeOutsideClickListener();
  }

  render() {
    return html`
      <sp-popover open placement="bottom-start" style="position: relative">
        <sp-menu label=${msg('Context menu')}>
          ${this.items.map(
            (item) =>
              html`<sp-menu-item @click=${() => this.handleItemClick(item)}
                >${item.label}</sp-menu-item
              >`
          )}
          ${
            this.items.length === 0
              ? html`<sp-menu-item disabled>${msg('No items available')}</sp-menu-item>`
              : ''
          }
        </sp-menu>
      </sp-popover>
    `;
  }
}

export function setupCustomContextMenu(props?: { delay?: number }) {
  const delay = typeof props?.delay === 'undefined' ? 1000 : props.delay;
  return {
    update(context: any) {
      if (context.data.type === 'contextmenu') {
        const element = context.data.element as HTMLElement | undefined;
        const menu = element?.querySelector<CustomMenuElement>(
          'custom-context-menu'
        );
        if (menu) {
          menu.items = context.data.items ?? context.data.list ?? [];
          menu.onHide = context.data.onHide;
          menu.show();
          menu.requestUpdate();
        }
        return {
          items: context.data.items ?? context.data.list ?? [],
          delay,
          searchBar: context.data.searchBar,
          onHide: context.data.onHide,
        };
      }
    },
    render(context: any) {
      if (context.data.type === 'contextmenu') {
        return html`<custom-context-menu
          .items=${context.data.items ?? context.data.list ?? []}
          .onHide=${context.data.onHide}
        ></custom-context-menu>`;
      }
    },
  };
}

if (!customElements.get('custom-context-menu')) {
  customElements.define('custom-context-menu', CustomMenuElement);
}
